/**
 * Font stacks for the rich text editor, aligned with:
 * - `src/app/layout.tsx` (Poppins, Merriweather, Denton Test via Next/font; Google Sans via Fontsource)
 * - `src/app/globals.css` (@font-face + @theme: WF Visual Sans, Circular Std, CSS vars)
 * - Admin shell: `src/app/(admin)/(administrator)/layout.tsx` uses `font-inter` (Tailwind / system UI)
 */

export type RichTextFontOption = {
  /** Stable `<select>` value */
  id: string;
  label: string;
  /** Passed to TipTap `setFontFamily` → inline `font-family` */
  cssValue: string;
};

export const RICH_TEXT_FONT_OPTIONS: readonly RichTextFontOption[] = [
  { id: "", label: "Default", cssValue: "" },
  {
    id: "poppins",
    label: "Poppins",
    cssValue: 'var(--font-poppins), Poppins, sans-serif',
  },
  {
    id: "merriweather",
    label: "Merriweather",
    cssValue: 'var(--font-merriweather), "Merriweather", serif',
  },
  // {
  //   id: "denton-test",
  //   label: "Denton Test",
  //   cssValue: 'var(--font-denton-test), "Denton Test", serif',
  // },
  {
    id: "google-sans",
    label: "Google Sans",
    cssValue: "var(--font-google-sans)",
  },
  // {
  //   id: "wf-visual-sans",
  //   label: "WF Visual Sans",
  //   cssValue: '"WF Visual Sans", sans-serif',
  // },
  {
    id: "circular-std",
    label: "Circular Std",
    cssValue: '"Circular Std", sans-serif',
  },
  {
    id: "inter",
    label: "Inter",
    cssValue: 'Inter, ui-sans-serif, system-ui, sans-serif',
  },
];

function normalizeFontFamilyForCompare(value: string): string {
  return value
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/['"]/g, "");
}

/**
 * Map `textStyle.fontFamily` from the editor (or parsed HTML) to a dropdown `id`.
 */
export function matchRichTextFontFamilyToOptionId(
  attr: string | null | undefined
): string {
  if (!attr?.trim()) return "";
  const normalizedAttr = normalizeFontFamilyForCompare(attr);

  for (const opt of RICH_TEXT_FONT_OPTIONS) {
    if (!opt.id || !opt.cssValue) continue;
    if (normalizeFontFamilyForCompare(opt.cssValue) === normalizedAttr) {
      return opt.id;
    }
  }

  for (const opt of RICH_TEXT_FONT_OPTIONS) {
    if (!opt.id || !opt.cssValue) continue;
    const first = opt.cssValue.split(",")[0]?.trim() ?? "";
    if (!first) continue;
    const nFirst = normalizeFontFamilyForCompare(first);
    if (nFirst && normalizedAttr.includes(nFirst)) {
      return opt.id;
    }
  }

  return "";
}

export function getRichTextFontOptionById(id: string): RichTextFontOption | undefined {
  return RICH_TEXT_FONT_OPTIONS.find((o) => o.id === id);
}
